<template>
    <div id="dashboard-page">
        <pre v-if="isDebug">{{
            {
                chartDataEfficiency,
            }
        }}</pre>
        <div class="top">
            <h2 class="employeeName">
                <span v-if="userDetails">
                    {{ userDetails.name }}
                </span>
            </h2>
            <ui5-switch
                :text-on="trans('pages.dashboard.switch_text_on')"
                :text-off="trans('pages.dashboard.switch_text_off')"
                :checked="selectedChart == CHART_SPEED"
                @change="
                    selectedChart = $event.target.checked
                        ? CHART_SPEED
                        : CHART_EFFICIENCY;
                    load();
                "
            ></ui5-switch>
            <actions-menu v-if="companyData && employeeData && userDetails">
                <actions-menu-item
                    :v4="true"
                    :action="exportData"
                    icon="table"
                    :label="trans('pages.dashboard.export_data')"
                />
            </actions-menu>
        </div>
        <div class="body">
            <div class="section chart">
                <loader
                    v-if="!loaded"
                    :cover="false"
                    :message="trans('pages.dashboard.loader_message')"
                />
                <bar-chart
                    v-else-if="selectedChart == CHART_SPEED && chartData"
                    :chart-data="chartData"
                    :chart-options="options"
                />

                <line-chart
                    v-else-if="
                        selectedChart == CHART_EFFICIENCY && chartDataEfficiency
                    "
                    :chart-data="chartDataEfficiency"
                    :chart-options="effiencyOptions"
                />
            </div>
            <div class="bottom-sections">
                <div class="section filters">
                    <div>
                        <div class="filters-title">
                            <h3>
                                <template v-if="selectedChart == CHART_SPEED">
                                    {{ trans('pages.dashboard.work_speed') }}
                                </template>
                                <template
                                    v-else-if="
                                        selectedChart == CHART_EFFICIENCY
                                    "
                                >
                                    {{ trans('pages.dashboard.efficiency') }}
                                </template>
                                {{ trans('pages.dashboard.displayed_for') }}
                            </h3>
                        </div>
                        <div class="filters-date-picker">
                            <ui5-daterange-picker
                                @click="
                                    $event.target.lastValue =
                                        $event.target.value;
                                    $event.target.openPicker();
                                "
                                @change="dateRangePickerChange"
                                :delimiter="DATE_DELIMITER"
                                :value="
                                    startDate +
                                    ' ' +
                                    DATE_DELIMITER +
                                    ' ' +
                                    endDate
                                "
                                :placeholder="
                                    trans(
                                        'pages.dashboard.date_picker_placeholder'
                                    )
                                "
                                @keyup.esc="
                                    $event.target.value =
                                        $event.target.lastValue
                                "
                                format-pattern="dd/MM/yyyy"
                            />
                        </div>
                        <div class="filters-buttons">
                            <button
                                class="filters-button"
                                id="month"
                                @click="changeDateFormat(DATE_FORMAT_MONTH)"
                            >
                                {{ trans('pages.dashboard.month') }}
                            </button>
                            <button
                                class="filters-button"
                                id="week"
                                @click="changeDateFormat(DATE_FORMAT_WEEK)"
                            >
                                {{ trans('pages.dashboard.week') }}
                            </button>
                            <button
                                class="filters-button"
                                id="day"
                                @click="changeDateFormat(DATE_FORMAT_DAY)"
                            >
                                {{ trans('pages.dashboard.day') }}
                            </button>
                        </div>
                    </div>
                </div>
                <div
                    v-if="selectedChart == CHART_SPEED"
                    class="section definition"
                >
                    <p
                        v-html="trans('pages.dashboard.work_speed_explanation')"
                    ></p>
                </div>
                <div
                    v-if="selectedChart == CHART_EFFICIENCY"
                    class="section definition"
                >
                    <p
                        v-html="trans('pages.dashboard.efficiency_explanation')"
                    ></p>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import IsDebugMixin from '../mixins/is-debug';
import IdParamMixin from '../mixins/id-param';
import dashboardApi from '../api/dashboard';
import { showErrorMessage } from '../dialogs';
import BarChart from '../components/BarChart';
import LineChart from '../components/LineChart';
import { mapGetters } from 'vuex';
import workstationApi from '../api/workstation';
import _ from 'lodash';
import { dateToDmY } from '../helpers/DateHelper';
import Loader from '../components/loader/Loader';
import userApi from '../api/user';
import ActionsMenu from '../components/ActionsMenu';
import ActionsMenuItem from '../components/ActionsMenuItem';
import * as Papa from 'papaparse';
import { saveAs } from 'file-saver';
import localization from '../mixins/localization';

const DATE_FORMAT_MONTH = 0;
const DATE_FORMAT_WEEK = 1;
const DATE_FORMAT_DAY = 2;

const DATE_DELIMITER = '→';

const CHART_EFFICIENCY = 1;
const CHART_SPEED = 0;

function saveAsCsv(data, filename) {
    const csv = Papa.unparse(data);

    const blob = new Blob([csv], {
        type: 'text/csv;charset=utf-8;',
    });

    saveAs(blob, filename);
}

function calculateIdealSeconds(set) {
    return _.sum(set.map((x) => parseInt(x.idealMinutes))) * 60;
}

function calculateSpentSeconds(set) {
    return _.sum(set.map((x) => parseInt(x.timeSpent)));
}

function toPercentage(val) {
    return (val * 100).toFixed(3);
}

function transformDate(hyphenDate) {
    const parts = hyphenDate.split('-');
    return parts[2] + '/' + parts[1] + '/' + parts[0];
}

export default {
    mixins: [IsDebugMixin, IdParamMixin('employeeIdParam'), localization],
    components: { BarChart, LineChart, Loader, ActionsMenu, ActionsMenuItem },
    mounted() {
        if (this.visitsOwnPage && !this.readOnly) {
            dashboardApi
                .startVisit()
                .then((response) => {
                    this.visitId = response.data.data.visitId;
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.dashboard.error_register_visit')
                    );
                });
        }
    },
    data() {
        return {
            selectedChart: CHART_SPEED,
            loaded: false,
            chartData: null,
            chartDataEfficiency: null,
            employeeDataIds: [],
            employeeSpeedPercentages: [],
            companyDataFiltered: [],
            companySpeedPercentages: [],
            totalSpeedPercentages: [],
            startDate: '',
            endDate: '',
            entries: null,
            visitId: null,
            exporting: false,
            workstations: [],
            workstationLabels: [],
            workstationIds: [],
            dateFormat: 0,
            employeeData: [],
            companyData: [],
            userDetails: null,
        };
    },
    computed: {
        ...mapGetters({
            userId: 'currentUser/getUserId',
        }),
        CHART_SPEED: () => CHART_SPEED,
        CHART_EFFICIENCY: () => CHART_EFFICIENCY,
        options() {
            return {
                maintainAspectRatio: false,
                title: {
                    display: true,
                    text: this.trans('pages.dashboard.work_speed_title'),
                },
                plugins: {
                    legend: {
                        display: true,
                    },
                    tooltip: {
                        callbacks: {
                            footer: (tooltipItems) => {
                                const item = tooltipItems[0];
                                const id = this.workstationIds[item.dataIndex];
                                const data = this[item.dataset.name];
                                const counts =
                                    this.getProductCountsByWorkstation(data);
                                const count = counts[id] || 0;
                                return this.trans(
                                    'pages.dashboard.products_count',
                                    { count: count }
                                );
                            },
                        },
                    },
                },
            };
        },
        effiencyOptions() {
            return {
                maintainAspectRatio: false,
                title: {
                    display: true,
                    text: this.trans('pages.dashboard.efficiency_title'),
                },
                plugins: {
                    legend: {
                        display: true,
                    },
                },
            };
        },
        isAdmin() {
            return this.$store.getters['currentUser/hasRight'](
                'administration'
            );
        },
        readOnly() {
            return this.$route.query.readOnly;
        },
        employeeIdParam() {
            return this.$route.query.employeeId;
        },
        ...mapGetters({
            userId: 'currentUser/getUserId',
            userName: 'currentUser/getUserName',
        }),
        visitsOwnPage() {
            return this.employeeIdParam == this.userId;
        },
        DATE_FORMAT_MONTH: () => DATE_FORMAT_MONTH,
        DATE_FORMAT_WEEK: () => DATE_FORMAT_WEEK,
        DATE_FORMAT_DAY: () => DATE_FORMAT_DAY,
        DATE_DELIMITER: () => DATE_DELIMITER,
    },
    beforeRouteLeave(to, from, next) {
        if (this.visitsOwnPage && this.visitId && !this.readOnly) {
            dashboardApi.endVisit(this.visitId).catch(() => {
                showErrorMessage(this.trans('pages.dashboard.error_end_visit'));
            });
        }
        next();
    },
    methods: {
        exportData() {
            const userDetails = this.userDetails;

            if (this.selectedChart == CHART_SPEED) {
                const grouper = (el) => el.workstationId + '_' + el.finishedAt;
                const groupedEmployeeData = _.groupBy(
                    this.employeeData,
                    grouper
                );
                const groupedCompanyData = _.groupBy(this.companyData, grouper);
                const usedWorkstationIds = Object.keys(groupedEmployeeData);

                const data = usedWorkstationIds.map((key) => {
                    const keyParts = key.split('_');
                    const workstationId = keyParts[0];
                    const finishedAt = keyParts[1];

                    const employeeData = groupedEmployeeData[key];
                    const companyData = groupedCompanyData[key];

                    const idealSeconds = calculateIdealSeconds(employeeData);
                    const spentSeconds = calculateSpentSeconds(employeeData);

                    return {
                        [this.trans('pages.dashboard.export_columns.employee')]:
                            userDetails.name,
                        [this.trans(
                            'pages.dashboard.export_columns.start_date'
                        )]: this.startDate,
                        [this.trans('pages.dashboard.export_columns.end_date')]:
                            this.endDate,
                        [this.trans('pages.dashboard.export_columns.date')]:
                            transformDate(finishedAt),
                        [this.trans(
                            'pages.dashboard.export_columns.workstation'
                        )]: this.workstations.find((x) => x.id == workstationId)
                            .label,
                        [this.trans(
                            'pages.dashboard.export_columns.product_count'
                        )]: employeeData.length,
                        [this.trans(
                            'pages.dashboard.export_columns.company_product_count'
                        )]: companyData.length,
                        [this.trans(
                            'pages.dashboard.export_columns.target_time_sec'
                        )]: idealSeconds,
                        [this.trans(
                            'pages.dashboard.export_columns.work_time_sec'
                        )]: spentSeconds,
                        [this.trans(
                            'pages.dashboard.export_columns.work_speed_percent'
                        )]: toPercentage(idealSeconds / spentSeconds),
                        [this.trans(
                            'pages.dashboard.export_columns.avg_work_speed_percent'
                        )]: toPercentage(
                            calculateIdealSeconds(companyData) /
                                calculateSpentSeconds(companyData)
                        ),
                    };
                });

                const sortedData = _.sortBy(
                    data,
                    this.trans('pages.dashboard.export_columns.date')
                );

                saveAsCsv(
                    sortedData,
                    this.trans('pages.dashboard.export_filenames.work_speed')
                );
            } else if (this.selectedChart == CHART_EFFICIENCY) {
                const chartData = this.chartDataEfficiency;
                const dates = chartData.labels;
                const employeeData = chartData.datasets.find(
                    (x) => x.name == 'employeeData'
                );
                const companyData = chartData.datasets.find(
                    (x) => x.name == 'companyData'
                );

                const companyEfficiencies = companyData.data;
                const employeeEfficiencies = employeeData.data;
                const punchClockValues = employeeData.punchClockValues;
                const workTimeValues = employeeData.workTimeValues;

                const data = Object.keys(dates).map((index) => {
                    return {
                        [this.trans('pages.dashboard.export_columns.employee')]:
                            userDetails.name,
                        [this.trans(
                            'pages.dashboard.export_columns.start_date'
                        )]: this.startDate,
                        [this.trans('pages.dashboard.export_columns.end_date')]:
                            this.endDate,
                        [this.trans('pages.dashboard.export_columns.date')]:
                            transformDate(dates[index]),
                        [this.trans(
                            'pages.dashboard.export_columns.production_seconds'
                        )]: punchClockValues[index],
                        [this.trans(
                            'pages.dashboard.export_columns.work_seconds'
                        )]: workTimeValues[index],
                        [this.trans(
                            'pages.dashboard.export_columns.efficiency'
                        )]: (employeeEfficiencies[index] || 0).toFixed(3),
                        [this.trans(
                            'pages.dashboard.export_columns.avg_efficiency'
                        )]: (companyEfficiencies[index] || 0).toFixed(3),
                    };
                });

                saveAsCsv(
                    data,
                    this.trans('pages.dashboard.export_filenames.efficiency')
                );
            }
        },
        loadSpeed() {
            const promises = [];
            promises.push(this.fetchEmployeeData());
            if (this.employeeIdParam) {
                promises.push(this.fetchCompanyData());
            }
            promises.push(this.fetchWorkstations());

            Promise.all(promises).then(() => {
                this.employeeCalculations();
                this.companyCalculations();
                this.workstationCalculations();
                this.refreshChartData();

                this.loaded = true;
            });
        },
        loadEfficiency() {
            Promise.all([
                this.fetchEfficiencyForCompany().then((r) => r.data.data),
                this.fetchEfficiencyForEmployee().then((r) => r.data.data),
            ]).then(([companyData, employeeData]) => {
                const keys = _.uniq([
                    ...Object.keys(companyData),
                    ...Object.keys(employeeData),
                ]);

                const companyValues = [];
                const employeeValues = [];
                const punchClockValues = [];
                const workTimeValues = [];
                const labels = [];
                keys.forEach((key) => {
                    const v1 = companyData[key].efficiency;
                    const v2 = employeeData[key].efficiency;

                    if (v1 || v2) {
                        labels.push(key);
                        companyValues.push(v1 * 100);
                        employeeValues.push(v2 * 100);
                        punchClockValues.push(employeeData[key].punchClock);
                        workTimeValues.push(employeeData[key].workTime);
                    }
                });

                this.chartDataEfficiency = {
                    labels: labels,
                    datasets: [
                        {
                            data: companyValues,
                            backgroundColor: ['rgba(136,136,136,0.5)'],
                            borderColor: ['rgba(136,136,136,0.2)'],
                            borderWidth: 1,
                            label: this.trans(
                                'pages.dashboard.chart_labels.company_efficiency'
                            ),
                            name: 'companyData',
                        },
                        {
                            data: employeeValues,
                            backgroundColor: ['rgba(255,153,41,1)'],
                            borderColor: ['rgba(255,153,41,0.2)'],
                            borderWidth: 1,
                            label: this.trans(
                                'pages.dashboard.chart_labels.personal_efficiency'
                            ),
                            name: 'employeeData',
                            punchClockValues,
                            workTimeValues,
                        },
                    ],
                };

                this.loaded = true;
            });
        },
        fetchEfficiencyForEmployee() {
            return dashboardApi.fetchEfficiency(
                this.employeeIdParam,
                this.startDate,
                this.endDate
            );
        },
        fetchEfficiencyForCompany() {
            return dashboardApi.fetchEfficiency(
                null,
                this.startDate,
                this.endDate
            );
        },
        dateRangePickerChange($event) {
            let valid = $event.detail.valid;
            if (valid) {
                const parts = $event.detail.value
                    .split(DATE_DELIMITER)
                    .map((x) => x.trim());
                if (parts.length != 2) {
                    valid = false;
                } else {
                    this.startDate = parts[0];
                    this.endDate = parts[1];
                    this.load();
                }
            }

            if (!valid) {
                $event.target.value = $event.target.lastValue;
            }
        },
        getProductCountsByWorkstation(data) {
            if (!data) {
                return null;
            }
            const results = {};
            const workstationIds = _.uniq(data.map((x) => x.workstationId));
            workstationIds.forEach((workstationId) => {
                const uniqueProducts = _.uniq(
                    data
                        .filter((x) => x.workstationId == workstationId)
                        .map((x) => x.productId)
                );
                results[workstationId] = uniqueProducts.length;
            });
            return results;
        },
        load() {
            if (!this.visitsOwnPage && !this.isAdmin) {
                return showErrorMessage(
                    this.trans('pages.dashboard.errors.no_access')
                );
            }
            this.clearData();
            if (this.startDate === '') {
                this.changeDateFormat(DATE_FORMAT_MONTH);
                return;
            }

            this.loaded = false;

            if (this.selectedChart == CHART_SPEED) {
                this.loadSpeed();
            } else if (this.selectedChart == CHART_EFFICIENCY) {
                this.loadEfficiency();
            }

            if (this.employeeIdParam) {
                this.fetchUserDetails();
            } else {
                this.userDetails = null;
            }
        },

        fetchEmployeeData() {
            return dashboardApi
                .fetch(this.employeeIdParam, this.startDate, this.endDate)
                .then((response) => {
                    this.employeeData = response.data.data;
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.dashboard.errors.fetch_user_data')
                    );
                });
        },

        fetchCompanyData() {
            return dashboardApi
                .fetch(undefined, this.startDate, this.endDate)
                .then((response) => {
                    this.companyData = response.data.data;
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.dashboard.errors.fetch_company_data')
                    );
                });
        },

        fetchWorkstations() {
            return workstationApi
                .fetchAllWorkstations()
                .then((response) => {
                    this.workstations = _.flatten(
                        Object.values(response.data.data)
                    );
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.dashboard.errors.fetch_workstations')
                    );
                });
        },

        employeeCalculations() {
            const employeeIdealMinutesSum = [];
            const employeeTimeSpentSum = [];
            this.employeeData.forEach((element) => {
                if (
                    this.employeeDataIds[this.employeeDataIds.length - 1] ==
                    element.workstationId
                ) {
                    employeeIdealMinutesSum[
                        employeeIdealMinutesSum.length - 1
                    ] =
                        Number(
                            employeeIdealMinutesSum[
                                employeeIdealMinutesSum.length - 1
                            ]
                        ) + Number(element.idealMinutes);
                    employeeTimeSpentSum[employeeTimeSpentSum.length - 1] =
                        Number(
                            employeeTimeSpentSum[
                                employeeTimeSpentSum.length - 1
                            ]
                        ) + Number(element.timeSpent);
                } else {
                    this.employeeDataIds.push(element.workstationId);
                    employeeIdealMinutesSum.push(element.idealMinutes);
                    employeeTimeSpentSum.push(element.timeSpent);
                }
            });

            let employeeIndex = 0;
            employeeTimeSpentSum.forEach((element) => {
                employeeTimeSpentSum[employeeIndex] = element / 60;
                employeeIndex++;
            });
            employeeIndex = 0;
            this.employeeDataIds.forEach(() => {
                this.employeeSpeedPercentages.push(
                    (employeeIdealMinutesSum[employeeIndex] /
                        employeeTimeSpentSum[employeeIndex]) *
                        100
                );
                employeeIndex++;
            });
        },

        companyCalculations() {
            const companyDataFiltered = [];
            this.companyData.forEach((element) => {
                if (
                    this.employeeDataIds.indexOf(element.workstationId) !== -1
                ) {
                    companyDataFiltered.push(element);
                }
            });

            const companyDataIds = [];
            const companyIdealMinutesSum = [];
            const companyTimeSpentSum = [];
            companyDataFiltered.forEach((element) => {
                if (
                    companyDataIds[companyDataIds.length - 1] ==
                    element.workstationId
                ) {
                    companyIdealMinutesSum[companyIdealMinutesSum.length - 1] =
                        Number(
                            companyIdealMinutesSum[
                                companyIdealMinutesSum.length - 1
                            ]
                        ) + Number(element.idealMinutes);
                    companyTimeSpentSum[companyTimeSpentSum.length - 1] =
                        Number(
                            companyTimeSpentSum[companyTimeSpentSum.length - 1]
                        ) + Number(element.timeSpent);
                } else {
                    companyDataIds.push(element.workstationId);
                    companyIdealMinutesSum.push(element.idealMinutes);
                    companyTimeSpentSum.push(element.timeSpent);
                }
            });

            let companyIndex = 0;
            companyTimeSpentSum.forEach((element) => {
                companyTimeSpentSum[companyIndex] = element / 60;
                companyIndex++;
            });

            companyIndex = 0;
            companyDataIds.forEach(() => {
                this.companySpeedPercentages.push(
                    (companyIdealMinutesSum[companyIndex] /
                        companyTimeSpentSum[companyIndex]) *
                        100
                );
                companyIndex++;
            });

            let totalsIndex = 0;
            this.employeeDataIds.forEach(() => {
                this.totalSpeedPercentages.push(
                    this.companySpeedPercentages[totalsIndex]
                );
                this.totalSpeedPercentages.push(
                    this.employeeSpeedPercentages[totalsIndex]
                );
                totalsIndex++;
            });
        },

        workstationCalculations() {
            this.workstations.sort((a, b) =>
                a.id > b.id ? 1 : b.id > a.id ? -1 : 0
            );
            this.workstations.forEach((element) => {
                this.employeeDataIds.forEach((id) => {
                    if (element.id == id) {
                        //this.workstationLabels.push('');
                        this.workstationLabels.push(element.label);
                        this.workstationIds.push(element.id);
                    }
                });
            });
        },

        fetchUserDetails() {
            if (this.employeeIdParam) {
                return userApi
                    .fetchUserDetails(this.employeeIdParam)
                    .then((response) => {
                        const details = response.data.data;
                        this.userDetails = details;
                    })
                    .catch(() => {
                        showErrorMessage(
                            this.trans(
                                'pages.dashboard.errors.fetch_user_details'
                            )
                        );
                    });
            } else {
                this.userDetails = null;
            }
        },

        refreshChartData() {
            this.chartData = {
                labels: this.workstationLabels,
                datasets: [
                    {
                        data: this.companySpeedPercentages,
                        backgroundColor: ['rgba(136,136,136,0.5)'],
                        borderColor: ['rgba(136,136,136,0.2)'],
                        borderWidth: 1,
                        label: this.trans(
                            'pages.dashboard.chart_labels.company_work_speed'
                        ),
                        name: 'companyData',
                    },
                    {
                        data: this.employeeSpeedPercentages,
                        backgroundColor: ['rgba(255,153,41,1)'],
                        borderColor: ['rgba(255,153,41,0.2)'],
                        borderWidth: 1,
                        label: this.trans(
                            'pages.dashboard.chart_labels.personal_work_speed'
                        ),
                        name: 'employeeData',
                    },
                ],
            };
        },

        changeDateFormat(format) {
            if (format == DATE_FORMAT_WEEK) {
                this.dateFormat = DATE_FORMAT_WEEK;

                var then = new Date();
                then.setDate(then.getDate() - 7);
                this.startDate = then;

                var now = new Date();
                this.endDate = now;
            } else if (format == DATE_FORMAT_DAY) {
                this.dateFormat = DATE_FORMAT_DAY;

                var today = new Date();
                this.startDate = today;
                this.endDate = today;
            } else {
                this.dateFormat = DATE_FORMAT_MONTH;

                var then = new Date();
                then.setMonth(then.getMonth() - 1);
                this.startDate = then;

                var now = new Date();
                this.endDate = now;
            }
            this.startDate = this.dateFormatter(this.startDate);
            this.endDate = this.dateFormatter(this.endDate);
            this.load();
        },

        dateFormatter(date) {
            return dateToDmY(date, '/');
        },

        clearData() {
            this.chartData = null;
            this.loaded = false;
            this.employeeDataIds = [];
            this.employeeSpeedPercentages = [];
            this.companySpeedPercentages = [];
            this.totalSpeedPercentages = [];
            this.workstations = [];
            this.workstationLabels = [];
            this.workstationIds = [];
            this.employeeData = [];
            this.companyData = [];
        },
    },
};
</script>
